import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { GetBillsFilter } from '@/modules/finance/bills/types/dto';
import { axiosInstance } from '@/utils/axios';

import { mapBillDetailsFromResponse } from '../mappers/bill-details.mapper';
import { mapBillSummariesFromResponse } from '../mappers/bill-summary.mapper';
import { BillDetails, BillListItem } from '../types/bills';
import {
  CreateBillsRequest,
  DeleteBillRequest,
  FindBillSummariesRequest,
  GetGeneratedBillsRequest,
  UpdateBillRequest,
} from '../types/dto/request';
import {
  BillDetailsResponse,
  BillMonthlySalaryAvailabilityResponse,
  BillSummaryResponse,
  GetGeneratedBillsResponse,
  UpdateBillResponse,
} from '../types/dto/response';

interface GetBillMonthlySalaryAvailabilityParams {
  memberId: number;
  startDate: string;
  endDate: string;
  currencyCode: string;
}

interface GetBillsParams {
  cursor?: number;
  filters: GetBillsFilter;
  /**
   * Override the page size. Defaults to DEFAULT_PAGE_SIZE. Used by the polling
   * fallback to reload all currently-loaded items in a single request.
   */
  pageSize?: number;
}

/**
 * Retrieves paginated list of bills with filters and search.
 * POST /api/bills/search
 *
 * @param params - Cursor and filters for the bills search
 * @returns Paginated list of bill list items
 */
export const getBills = async ({
  cursor,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
}: GetBillsParams): Promise<PaginatedResponse<BillListItem>> => {
  const endpoint = '/api/bills/search';

  const requestBody: FindBillSummariesRequest = {
    ...filters,
    pagination: {
      pageSize,
      cursor: cursor,
    },
  };

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.BILLS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: {
        items: BillSummaryResponse[];
        pagination: {
          total: number;
          cursor: number | null;
        };
      };
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );

  return new PaginatedResponse({
    items: mapBillSummariesFromResponse(apiData.items || []),
    pagination: apiData.pagination || { total: 0, cursor: null },
  });
};

/** Retrieves one bill's complete details. GET /api/bills/:billId */
export const getBillDetails = async (billId: number): Promise<BillDetails> => {
  const endpoint = `/api/bills/${billId}`;

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.BILL_DETAILS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: BillDetailsResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );

  return mapBillDetailsFromResponse(apiData);
};

/** Resolves whether a member can use monthly-salary billing for the edited bill period. */
export const getBillMonthlySalaryAvailability = async ({
  memberId,
  startDate,
  endDate,
  currencyCode,
}: GetBillMonthlySalaryAvailabilityParams) => {
  const endpoint = '/api/bills/monthly-salary-availability';

  return withApiErrorHandler(endpoint, ApiErrorCode.BILL_DETAILS_LOAD_FAILED, () =>
    axiosInstance.get<{ data: BillMonthlySalaryAvailabilityResponse }>(endpoint, {
      params: { memberId, startDate, endDate, currencyCode },
    })
  );
};

/** Submits a full bill replacement and schedules its QuickBooks synchronization. */
export const updateBill = async ({
  billId,
  request,
}: {
  billId: number;
  request: UpdateBillRequest;
}): Promise<UpdateBillResponse> => {
  const endpoint = `/api/bills/${billId}`;

  return withApiErrorHandler(endpoint, ApiErrorCode.BILL_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateBillResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, request)
  );
};

/**
 * Creates new bills for the organization.
 * POST /api/bills
 *
 * @param request - Bill creation data
 */
export const createBills = async (request: CreateBillsRequest): Promise<void> => {
  const endpoint = '/api/bills';

  await withApiErrorHandler(endpoint, ApiErrorCode.BILLS_CREATE_FAILED, () =>
    axiosInstance.post<{
      data: never;
      error?: { status: number; message: string; code: string };
    }>(endpoint, request)
  );
};

export const getGeneratedBills = async (
  params: GetGeneratedBillsRequest
): Promise<GetGeneratedBillsResponse> => {
  const endpoint = '/api/bills/generated';

  return await withApiErrorHandler(endpoint, ApiErrorCode.BILLS_GENERATED_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: GetGeneratedBillsResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
      },
    })
  );
};

/**
 * Deletes a bill by ID.
 * DELETE /api/bills
 *
 * @param params - Bill ID to delete
 */
export const deleteBill = async (params: DeleteBillRequest): Promise<void> => {
  const endpoint = '/api/bills';

  await withApiErrorHandler(endpoint, ApiErrorCode.BILL_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, {
      params: {
        billId: params.billId,
      },
    })
  );
};

/**
 * Retries a bill that failed to generate.
 * POST /api/bills/:billId/retry
 *
 * @param billId - ID of the bill to retry
 */
export const retryBill = async (billId: number): Promise<void> => {
  const endpoint = `/api/bills/${billId}/retry`;

  await withApiErrorHandler(endpoint, ApiErrorCode.BILL_RETRY_FAILED, () =>
    axiosInstance.post(endpoint)
  );
};
